import { create } from 'zustand';
import { SupplierDeliveryWindowCustomer, SupplierDeliveryWindowUpdateRequest } from '@api/generated/search-service';
import { supplierDeliveryWindowsApi } from '@api/api-config';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';

interface EditingCustomer {
  organizationId: number;
  organizationName: string;
  customerNumber?: string;
  dayLimits: Array<{ day: string; limit?: number }>;
  deliveryTime?: string | { hour?: number; minute?: number };
}

interface SupplierDeliveryWindowsState {
  customers: SupplierDeliveryWindowCustomer[];
  isLoading: boolean;
  error: string | null;

  // Edit modal state
  isEditModalOpen: boolean;
  editingCustomer: EditingCustomer | null;
  isSaving: boolean;
  saveError: string | null;

  fetchDeliveryWindows: () => Promise<void>;
  clearError: () => void;

  // Edit modal actions
  openEditModal: (customer: SupplierDeliveryWindowCustomer) => void;
  closeEditModal: () => void;
  saveDeliveryWindow: (data: SupplierDeliveryWindowUpdateRequest) => Promise<boolean>;
  clearSaveError: () => void;
}

export const useSupplierDeliveryWindowsStore = create<SupplierDeliveryWindowsState>((set, get) => ({
  customers: [],
  isLoading: false,
  error: null,

  // Edit modal state
  isEditModalOpen: false,
  editingCustomer: null,
  isSaving: false,
  saveError: null,

  fetchDeliveryWindows: async () => {
    set({ isLoading: true, error: null });

    try {
      const { orgId: supplierId, portalId } = useOrgPortal.getState();

      if (!supplierId || !portalId) {
        set({ error: 'Dodavatel nebo portál nebyl nalezen', isLoading: false });
        return;
      }

      const response = await supplierDeliveryWindowsApi.getSupplierDeliveryWindows({
        supplierId,
        portalId,
      });

      set({
        customers: response.data?.customers || [],
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst závozová okna';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  openEditModal: (customer: SupplierDeliveryWindowCustomer) => {
    set({
      isEditModalOpen: true,
      editingCustomer: {
        organizationId: customer.organizationId,
        organizationName: customer.organizationName,
        customerNumber: customer.customerNumber,
        dayLimits: customer.dayLimits.map(dl => ({ day: dl.day, limit: dl.limit })),
        deliveryTime: customer.deliveryTime,
      },
      saveError: null,
    });
  },

  closeEditModal: () => {
    set({
      isEditModalOpen: false,
      editingCustomer: null,
      saveError: null,
    });
  },

  saveDeliveryWindow: async (data: SupplierDeliveryWindowUpdateRequest) => {
    const { editingCustomer } = get();
    if (!editingCustomer) return false;

    set({ isSaving: true, saveError: null });

    try {
      const { orgId: supplierId, portalId } = useOrgPortal.getState();

      if (!supplierId || !portalId) {
        set({ saveError: 'Dodavatel nebo portál nebyl nalezen', isSaving: false });
        return false;
      }

      await supplierDeliveryWindowsApi.updateSupplierDeliveryWindow({
        supplierId,
        portalId,
        organizationId: editingCustomer.organizationId,
        supplierDeliveryWindowUpdateRequest: data,
      });

      set({ isSaving: false });

      // Refresh data
      get().fetchDeliveryWindows();

      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se uložit změny';
      set({
        saveError: errorMessage,
        isSaving: false,
      });
      return false;
    }
  },

  clearSaveError: () => {
    set({ saveError: null });
  },
}));
